/**
 * `contract-type-proposals` — the Admin's type-decision list · CLM-110 (v2),
 * CLM-118 (v3). The propose-only slice (R-110-1 … R-110-8, #198) built v1; the
 * decision (R-110-9 … R-110-28, `phase4-specs/CLM-110-ai-decided-contract-type.md`)
 * made it the list an Admin decides from (R-110-22); D-110-3
 * (`phase4-specs/CLM-118-type-filename-then-ai.md`, R-118-16) adds Type pending,
 * the AI-decided types, the auto-confirmed families and the possible amendments.
 *
 * One read-only `GET /v1/admin/contract-type-proposals` behind the Admin
 * **Type proposals** page and the Needs Attention panel, gate `admin:configure`
 * (D-110-2). One scoped statement, folded in memory, so the self-checking
 * arithmetic below holds under READ COMMITTED. It takes no parameters, so no
 * page can narrow it by tag.
 *
 * ## What it lists
 *
 *  - `undecided` — every **agreement** whose type is not settled (`provisional`
 *    or `pending`) and that the AI has read or will never read, by reason: AI
 *    disagrees (`ai_disagrees`, and `ai_no_type` — an answer with no usable
 *    type, R-110-28), AI agrees (`ai_agrees`; of which `ai_decided` are the
 *    AI's own type applied by case 2), Type pending's own reasons
 *    (`ai_sow_needs_number`, `ai_type_not_applied`), and not read by AI
 *    (`restricted`, `awaiting_rerun`, `no_ai_proposal`). The `no_ai_proposal`
 *    list is capped at the newest {@link CONTRACT_TYPE_NO_AI_PROPOSAL_LIST_CAP},
 *    pending rows first, with its full count: the legacy corpus starts
 *    provisional (R-110-13) and can be long.
 *  - `confirmed_newer_disagreements` — settled families (`confirmed` by an Admin,
 *    or `auto_confirmed` because the file name and the AI agreed) in which a
 *    reading **newer** than the settlement disagrees with the type, or has no
 *    usable one: the agreement's own reading, or any amendment's. Each carries
 *    exactly one of `decision` (an Admin's) and `auto_confirmation` (the act that
 *    names nobody, never a decision).
 *  - `amendment_signals` — agreements flagged "possibly an amendment" (R-118-14).
 *  - counts of agreements **awaiting the AI**, Type **pending**, and
 *    **auto-confirmed**.
 *
 * An amendment never stands as an undecided agreement: its type follows its
 * agreement (R-110-15), so its reading is nested under the agreement and decided
 * with the family.
 *
 * ## The totals are agreement, not accuracy (R-110-7, kept)
 *
 * `agreements` / `disagreements` compare each agreement's proposal with its type
 * **now** — itself a guess until confirmed — so the page calls it agreement with
 * the current type. FR-34's accuracy is CLM-107's, over the Admins' counted
 * decisions (R-110-18). `not_compared` counts the proposals on **amendments**
 * and, since CLM-118, on **Type pending** agreements: a placeholder is not a
 * type to agree with.
 *
 * ## Scope
 *
 * Every row and every total is scoped on the contract's effective key before it
 * is counted (rules 9, 10). `scope_kind` tells the page when the figures cover
 * only part of the corpus.
 */
import { z } from 'zod';
import { ContractTypeAutoConfirmationSchema, ContractTypeDecisionSeenSchema, ContractTypeDecisionSummarySchema, } from '../domain/contract.js';
import { ContractKeySchema, IsoTimestampUtcSchema } from '../primitives.js';
import { AmendmentSignalNameSchema, ContractTypeConfirmationSchema, ContractTypeDecidedBySchema, ContractTypeNameEvidenceSchema, ContractTypeProposalSchema, ContractTypeSchema, LifecycleStatusSchema, } from '../vocabulary.js';
/**
 * The `field_name` on the `AIExtraction` row that records a reading (R-110-4),
 * and on the Admin decision's `HumanVerification` row (R-110-17). Dotted like
 * every audit field name (`FieldNameSchema`), and deliberately not a
 * `ContractDetail` path: no `field_verification` row is ever written for it, and
 * no admin field may reuse it.
 */
export const CONTRACT_TYPE_PROPOSAL_FIELD = 'classification.contract_type';
export const ContractTypeProposalsRequestSchema = z.object({}).strict();
/** R-110-22 — the `no_ai_proposal` list shows the newest this many, and counts them all. */
export const CONTRACT_TYPE_NO_AI_PROPOSAL_LIST_CAP = 200;
/**
 * The reasons an undecided agreement is **listed** under: every provisional
 * reason except `awaiting_ai`, which is only counted (nothing to decide yet).
 * CLM-118 appends Type pending's two and `awaiting_rerun`.
 */
export const CONTRACT_TYPE_UNDECIDED_REASONS = [
    'ai_disagrees',
    'ai_no_type',
    'ai_agrees',
    'restricted',
    'no_ai_proposal',
    'ai_sow_needs_number',
    'ai_type_not_applied',
    'awaiting_rerun',
];
export const ContractTypeUndecidedReasonSchema = z.enum(CONTRACT_TYPE_UNDECIDED_REASONS);
/** Why a settled family is listed again: its newer reading disagrees, or has no usable type. */
export const CONTRACT_TYPE_NEWER_READING_REASONS = ['ai_disagrees', 'ai_no_type'];
const AuditIdSchema = z.string().regex(/^\d{1,19}$/);
/**
 * The AI's **latest** reading of one contract — the newest `extraction-pipeline`
 * `AIExtraction` row for {@link CONTRACT_TYPE_PROPOSAL_FIELD}. `proposed_type`
 * is `null` exactly for an answer outside the vocabulary (`ai_no_type`,
 * R-110-28). Model and schema version are read from that row (CR-08).
 */
export const ContractTypeReadingSchema = z
    .object({
    audit_id: AuditIdSchema,
    proposed_type: ContractTypeProposalSchema.nullable(),
    extraction_model: z.string().min(1).max(120),
    extraction_schema_version: z.string().min(1).max(20),
    read_utc: IsoTimestampUtcSchema,
})
    .strict();
/** One amendment of a listed agreement, with its own latest reading (shown, never compared). */
export const ContractTypeAmendmentReadingSchema = z
    .object({
    contract_key: ContractKeySchema,
    proposal: ContractTypeReadingSchema.nullable(),
})
    .strict();
const rowShape = {
    contract_key: ContractKeySchema,
    /** The primary party's cached display name, when there is one. */
    counterparty_name: z.string().min(1).max(200).nullable(),
    contract_type: ContractTypeSchema,
    /** How the type was decided; `null` = recorded before CLM-110. */
    contract_type_source: ContractTypeDecidedBySchema.nullable(),
    sow_number: z.string().max(32).nullable(),
    lifecycle_status: LifecycleStatusSchema,
    processing_restricted: z.boolean(),
    /** The agreement's latest reading, or `null` when the AI has not read it. */
    proposal: ContractTypeReadingSchema.nullable(),
    /** The family's amendments, in key order. */
    amendments: z.array(ContractTypeAmendmentReadingSchema),
    /** The current base for its type — retyping it is `409 current_base_contract` (R-110-21). */
    is_current_base: z.boolean(),
    /** Posted back unchanged by the decision dialog (R-110-17). */
    seen: ContractTypeDecisionSeenSchema,
};
/** `seen` is the row as shown: the type, the latest reading, every family key and each amendment's reading. */
function seenMatchesRow(row) {
    const amendmentReadings = row.amendments
        .map((amendment) => amendment.proposal?.audit_id)
        .filter((id) => id !== undefined);
    const family = [row.contract_key, ...row.amendments.map((amendment) => amendment.contract_key)].sort();
    return (row.seen.contract_type === row.contract_type &&
        row.seen.proposal_audit_id === (row.proposal?.audit_id ?? null) &&
        row.seen.proposed_type === (row.proposal?.proposed_type ?? null) &&
        family.length === row.seen.family_contract_keys.length &&
        family.every((key, index) => row.seen.family_contract_keys[index] === key) &&
        amendmentReadings.length === row.seen.amendment_proposal_audit_ids.length &&
        amendmentReadings.every((id) => row.seen.amendment_proposal_audit_ids.includes(id)));
}
/**
 * The reason the row's own reading and confirmation give — the API's fold and
 * this refine cannot disagree. A Type pending row is never listed as agreeing or
 * disagreeing with its placeholder (CLM-118).
 */
function reasonMatchesReading(row) {
    const reading = row.proposal;
    const pending = row.seen.confirmation === 'pending';
    const provisional = row.seen.confirmation === 'provisional';
    switch (row.reason) {
        case 'ai_no_type':
            return reading !== null && reading.proposed_type === null;
        case 'ai_disagrees':
            return provisional && reading !== null && reading.proposed_type !== null && reading.proposed_type !== row.contract_type;
        case 'ai_agrees':
            return provisional && reading !== null && reading.proposed_type === row.contract_type;
        case 'ai_sow_needs_number':
            return pending && reading !== null && reading.proposed_type === 'SOW';
        case 'ai_type_not_applied':
            return pending && reading !== null && reading.proposed_type !== null && reading.proposed_type !== 'SOW';
        case 'restricted':
            return reading === null && row.processing_restricted;
        case 'no_ai_proposal':
        case 'awaiting_rerun':
            return reading === null && !row.processing_restricted;
        default:
            return false;
    }
}
/** One undecided agreement: provisional or Type pending, and read by the AI or never to be read by it. */
export const ContractTypeUndecidedRowSchema = z
    .object({
    ...rowShape,
    reason: ContractTypeUndecidedReasonSchema,
    /** CLM-118 (R-118-3) — what the file-name rule found at intake; `null` = never recorded. */
    name_evidence: ContractTypeNameEvidenceSchema.nullable(),
    /**
     * CLM-121 (R-121-15) — the type's auto-approval sent this AI-decided type to
     * an Admin as its 1-in-5 safety sample (its latest reading's act records
     * `sampled`). Correcting it pauses auto-approval.
     */
    auto_approval_sample: z.boolean(),
})
    .strict()
    /* `isAiDecidedRow`, restated: its parameter type is this schema's, so calling it here would be circular. */
    .refine((row) => !row.auto_approval_sample || (row.reason === 'ai_agrees' && row.contract_type_source === 'ai_proposed'), {
    message: 'a safety sample is an AI-decided row (CLM-121)',
    path: ['auto_approval_sample'],
})
    .refine(reasonMatchesReading, { message: 'the reason is the one the latest reading gives', path: ['reason'] })
    .refine((row) => row.seen.confirmation === 'provisional' || row.seen.confirmation === 'pending', {
    message: 'an undecided agreement was seen provisional or pending',
    path: ['seen', 'confirmation'],
})
    .refine(seenMatchesRow, { message: 'seen is the row as listed', path: ['seen'] });
/** Whether an undecided row is one the AI decided (case 2): its applied reading, still provisional. */
export function isAiDecidedRow(row) {
    return row.reason === 'ai_agrees' && row.contract_type_source === 'ai_proposed';
}
/**
 * The family's readings — the agreement's and every amendment's — that are
 * newer than the settlement and disagree with the type, or have no usable one.
 * The fold lists a settled family exactly when this is non-empty. The settlement
 * is the Admin's decision, or the act that confirmed the type by agreement.
 */
function newerDisagreeingReadings(row) {
    const settledUtc = row.decision?.decided_utc ?? row.auto_confirmation?.settled_utc;
    if (settledUtc === undefined)
        return [];
    const settled = Date.parse(settledUtc);
    return [row.proposal, ...row.amendments.map((amendment) => amendment.proposal)].filter((reading) => reading !== null && Date.parse(reading.read_utc) > settled && reading.proposed_type !== row.contract_type);
}
/**
 * A settled family in which a reading newer than the settlement disagrees with
 * the type: `ai_disagrees` when any such reading names another type,
 * `ai_no_type` when none of them has a usable type (R-110-28). Exactly one of
 * `decision` (seen `confirmed`) and `auto_confirmation` (seen `auto_confirmed`)
 * is set — a type nobody checked is never shown with a decision (B8).
 */
export const ContractTypeConfirmedNewerRowSchema = z
    .object({
    ...rowShape,
    reason: z.enum(CONTRACT_TYPE_NEWER_READING_REASONS),
    decision: ContractTypeDecisionSummarySchema.nullable(),
    auto_confirmation: ContractTypeAutoConfirmationSchema.nullable(),
})
    .strict()
    .refine((row) => {
    const newer = newerDisagreeingReadings(row);
    if (newer.length === 0)
        return false;
    const named = newer.some((reading) => reading.proposed_type !== null);
    return row.reason === (named ? 'ai_disagrees' : 'ai_no_type');
}, { message: 'the reason is the one the family’s newer readings give', path: ['reason'] })
    .refine((row) => (row.seen.confirmation === 'confirmed' && row.decision !== null && row.auto_confirmation === null) ||
    (row.seen.confirmation === 'auto_confirmed' && row.auto_confirmation !== null && row.decision === null), { message: 'a confirmed family carries its decision, an auto-confirmed one its act, never both', path: ['decision'] })
    .refine((row) => row.seen.confirmed_audit_id === (row.decision ?? row.auto_confirmation)?.audit_id, {
    message: 'a settled family was seen settled, by the row that settled it',
    path: ['seen', 'confirmed_audit_id'],
})
    .refine(seenMatchesRow, { message: 'seen is the row as listed', path: ['seen'] });
/**
 * CLM-118 (R-118-14) — an agreement flagged "possibly an amendment filed on its
 * own": an amendment word in its name, or a reading that names an agreement it
 * amends or replaces. Dismissed with `dismiss-amendment-signal`.
 */
export const ContractTypeAmendmentSignalRowSchema = z
    .object({
    contract_key: ContractKeySchema,
    counterparty_name: z.string().min(1).max(200).nullable(),
    contract_type: ContractTypeSchema,
    contract_type_confirmation: ContractTypeConfirmationSchema,
    lifecycle_status: LifecycleStatusSchema,
    name_word: AmendmentSignalNameSchema.nullable(),
    amends_reference: z.string().min(1).max(120).nullable(),
    supersedes_reference: z.string().min(1).max(120).nullable(),
    filed_utc: IsoTimestampUtcSchema,
})
    .strict()
    .refine((row) => row.name_word !== null || row.amends_reference !== null || row.supersedes_reference !== null, { message: 'a flag has a word or a reference behind it', path: ['name_word'] });
const Count = z.number().int().min(0);
/** The reasons whose every row is listed — every one but the capped `no_ai_proposal`. */
const FULLY_LISTED_REASONS = [
    'ai_disagrees',
    'ai_agrees',
    'ai_no_type',
    'restricted',
    'ai_sow_needs_number',
    'ai_type_not_applied',
    'awaiting_rerun',
];
export const ContractTypeProposalsResponseSchema = z
    .object({
    generated_utc: IsoTimestampUtcSchema,
    scope_kind: z.enum(['all', 'practices']),
    totals: z
        .object({
        /** Contracts in scope that carry a proposal — agreements and amendments. */
        proposals: Count,
        /** An agreement's proposal equals its type now (R-110-7: agreement, not accuracy). */
        agreements: Count,
        /** An agreement's proposal differs from its type now. */
        disagreements: Count,
        /** Proposals on amendments, and on Type pending agreements: never compared on their own. */
        not_compared: Count,
        /* The undecided agreements, by reason — the rows below. */
        ai_disagrees: Count,
        ai_agrees: Count,
        ai_no_type: Count,
        restricted: Count,
        /** Every one, though the list is capped. */
        no_ai_proposal: Count,
        /** How many of them are listed: min(no_ai_proposal, the cap). */
        no_ai_proposal_listed: Count,
        /** CLM-118 — Type pending agreements the AI read as a SOW: the Admin adds the number. */
        ai_sow_needs_number: Count,
        /** CLM-118 — Type pending agreements whose AI type a guard held. */
        ai_type_not_applied: Count,
        /** CLM-118 — waiting for a re-run (CLM-79) or an alias decision. */
        awaiting_rerun: Count,
        /** Agreements not yet past extraction — counted, not listed. */
        awaiting_ai: Count,
        /**
         * CLM-118 — Type pending agreements an Admin can decide now, listed or not
         * (the `no_ai_proposal` cap may leave some out). Those still awaiting the AI
         * are in `awaiting_ai`.
         */
        pending: Count,
        /** CLM-118 — of `ai_agrees`, the AI's own type applied by case 2 ("AI decided — verify"). */
        ai_decided: Count,
        /** Agreements an Admin has confirmed. */
        confirmed: Count,
        /**
         * CLM-118 / CLM-121 — agreements settled with no Admin, either kind: the file
         * name and the AI agree, or approved by policy (`policy_approved`).
         */
        auto_confirmed: Count,
        confirmed_newer_disagreements: Count,
        /** CLM-118 — agreements flagged "possibly an amendment". */
        amendment_signals: Count,
        /** CLM-121 (R-121-15) — the listed rows that are auto-approval safety samples. */
        auto_approval_samples: Count,
        /** CLM-121 — of `auto_confirmed`, the agreements approved by policy (`auto_approval_policy`). */
        policy_approved: Count,
    })
        .strict(),
    /**
     * AI disagrees first (with `ai_no_type`), then AI agrees, then Type pending's
     * own reasons, then not read by AI (restricted, awaiting a re-run, never read
     * — pending rows first within the capped list); newest reading first within
     * each.
     */
    undecided: z.array(ContractTypeUndecidedRowSchema),
    confirmed_newer_disagreements: z.array(ContractTypeConfirmedNewerRowSchema),
    /** CLM-118 — newest filed first. */
    amendment_signals: z.array(ContractTypeAmendmentSignalRowSchema),
})
    .strict()
    .refine((body) => body.totals.proposals === body.totals.agreements + body.totals.disagreements + body.totals.not_compared, {
    message: 'proposals is agreements + disagreements + not_compared',
    path: ['totals', 'proposals'],
})
    .refine((body) => FULLY_LISTED_REASONS.every((reason) => body.undecided.filter((row) => row.reason === reason).length === body.totals[reason]), { message: 'every undecided agreement is listed under its reason', path: ['undecided'] })
    .refine((body) => body.totals.no_ai_proposal_listed ===
    Math.min(body.totals.no_ai_proposal, CONTRACT_TYPE_NO_AI_PROPOSAL_LIST_CAP) &&
    body.undecided.filter((row) => row.reason === 'no_ai_proposal').length === body.totals.no_ai_proposal_listed, { message: `no_ai_proposal lists the newest ${String(CONTRACT_TYPE_NO_AI_PROPOSAL_LIST_CAP)}`, path: ['totals', 'no_ai_proposal_listed'] })
    .refine((body) => {
    const listed = body.undecided.filter((row) => row.seen.confirmation === 'pending').length;
    return (listed <= body.totals.pending &&
        (body.totals.no_ai_proposal_listed !== body.totals.no_ai_proposal || listed === body.totals.pending));
}, { message: 'every Type pending agreement is listed unless the no_ai_proposal cap left it out', path: ['totals', 'pending'] })
    .refine((body) => body.undecided.filter(isAiDecidedRow).length === body.totals.ai_decided, {
    message: 'ai_decided counts the listed AI-decided rows',
    path: ['totals', 'ai_decided'],
})
    .refine((body) => body.amendment_signals.length === body.totals.amendment_signals, {
    message: 'every flagged agreement is listed',
    path: ['totals', 'amendment_signals'],
})
    .refine((body) => body.confirmed_newer_disagreements.length === body.totals.confirmed_newer_disagreements &&
    body.totals.confirmed_newer_disagreements <= body.totals.confirmed + body.totals.auto_confirmed, { message: 'every settled family with a newer disagreeing reading is listed', path: ['confirmed_newer_disagreements'] })
    .refine((body) => body.undecided.filter((row) => row.auto_approval_sample).length === body.totals.auto_approval_samples, {
    message: 'auto_approval_samples counts the listed safety samples (CLM-121)',
    path: ['totals', 'auto_approval_samples'],
})
    .refine((body) => body.totals.policy_approved <= body.totals.auto_confirmed, {
    message: 'policy_approved is a part of auto_confirmed (CLM-121)',
    path: ['totals', 'policy_approved'],
});
//# sourceMappingURL=contract-type-proposals.js.map